import { useEffect, useState } from 'react'
import { ApiError } from './api'
import type { Identity } from './identity'

export type Resource<T> =
  | { state: 'loading' }
  | { state: 'ready'; value: T }
  | { state: 'failed'; code: string; message: string }

/**
 * One request, with the states a screen actually has to draw.
 *
 * `failed` carries the server's own code and message rather than a generic string. A caller who
 * is told "not permitted" can be helped; a caller who is told "something went wrong" cannot, and
 * neither can the person they phone about it.
 */
export function useResource<T>(
  load: (identity: Identity, signal: AbortSignal) => Promise<T>,
  identity: Identity | null,
): Resource<T> {
  const [resource, setResource] = useState<Resource<T>>({ state: 'loading' })
  const key = identity ? `${identity.organizationSlug}|${identity.persona ?? identity.idToken}` : ''

  useEffect(() => {
    if (!identity) return
    const controller = new AbortController()
    setResource({ state: 'loading' })
    load(identity, controller.signal)
      .then((value) => setResource({ state: 'ready', value }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        const failure =
          error instanceof ApiError
            ? { code: error.code, message: error.message }
            : { code: 'UNKNOWN', message: String(error) }
        setResource({ state: 'failed', ...failure })
      })
    return () => controller.abort()
    // `key` stands in for the identity: a new organization or a new token is a new request.
  }, [key, identity, load])

  return resource
}
